/**
 * useDriverTracking.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Custom React hook that is a complete, idiomatic conversion of
 * admin_dashboard_tracking.js into a lifecycle-safe hook.
 *
 * Key guarantees:
 *  • Leaflet is loaded on demand (once per session) via a Promise-based loader.
 *    The loader deduplicates concurrent callers — identical to the original
 *    _leafletQueue pattern but typed and Promise-based.
 *  • All side-effects (map instance, marker, Firestore listener, local poll
 *    timer) are registered inside useEffect and torn down in the cleanup
 *    function so React StrictMode double-mount is safe.
 *  • The map is mounted into a ref-forwarded <div> — no document.getElementById.
 *  • completionLocation is written back to localStorage (local-only mode)
 *    exactly as the original saveCompletionLocation() did.
 *  • Firestore path mirrors the original: jobs/{jobId}/driverLocation and
 *    jobs/{jobId}/completionLocation. When no Firestore backend is
 *    configured, driverLocation is instead read from the job's
 *    localStorage record — populated by the driver's own device in
 *    real time via useLiveLocationBroadcast()/watchPosition(). Either
 *    way the marker always reflects the driver's REAL GPS position,
 *    never a simulated one.
 *
 * FIX (blank/white map inside the Job Detail modal):
 *  The modal opens with a CSS transition, so at the moment initMap() first
 *  runs the map container can still be 0×0 (or mid-transition). A single
 *  requestAnimationFrame + invalidateSize() call was not enough to catch
 *  that — Leaflet would compute tile positions against a stale size and
 *  render nothing. initMap() now retries invalidateSize() a few times
 *  over ~600ms (covering the modal's open transition) instead of once.
 *
 * Usage:
 *   const { mapRef, trackingState } = useDriverTracking({ job });
 *   return <div ref={mapRef} style={{ height: 260 }} />;
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  useRef,
  useEffect,
  useState,
  useCallback,
  type RefObject,
} from 'react';
import { trackingApi } from './api';

// ─── Minimal Leaflet type stubs (CDN global, not an npm dependency) ──────────
// Full types available via `npm i -D @types/leaflet` if desired.
declare global {
  interface Window {
    L?: LeafletStatic;
    db?: FirestoreDb; // optional Firestore v8 compat instance
  }
}

interface LeafletLatLng { lat: number; lng: number }
interface LeafletMarker {
  setLatLng(ll: [number, number]): void;
  remove(): void;
}
interface LeafletMap {
  panTo(ll: [number, number]): void;
  remove(): void;
  invalidateSize(): void;
}
interface LeafletDivIcon { /* opaque */ }
interface LeafletStatic {
  map(
    el: HTMLElement,
    opts: { center: [number, number]; zoom: number; zoomControl: boolean; attributionControl: boolean }
  ): LeafletMap;
  tileLayer(url: string, opts: Record<string, unknown>): { addTo(m: LeafletMap): void };
  marker(
    ll: [number, number],
    opts: { icon: LeafletDivIcon }
  ): LeafletMarker & { addTo(m: LeafletMap): LeafletMarker };
  divIcon(opts: { html: string; className: string; iconSize: [number, number]; iconAnchor: [number, number] }): LeafletDivIcon;
}

// Firestore v8 compat stubs
interface FirestoreDocSnap {
  exists: boolean;
  data(): Record<string, unknown>;
}
interface FirestoreDocRef {
  onSnapshot(
    cb: (snap: FirestoreDocSnap) => void,
    errCb: (e: unknown) => void
  ): () => void;
  get(): Promise<FirestoreDocSnap>;
  update(data: Record<string, unknown>): Promise<void>;
}
interface FirestoreDb {
  collection(path: string): { doc(id: string): FirestoreDocRef };
}

// ─── Public types ─────────────────────────────────────────────────────────────
export interface CompletionLocation {
  lat: number;
  lng: number;
  savedAt: string;
  /** Horizontal accuracy radius in meters of the fix, if known. */
  accuracy?: number;
}

export interface TrackingJob {
  id: string;
  status: 'pending' | 'in_progress' | 'completed';
  auditStartGps?: string | null;
  /** GPS ตอนกดจบงาน (จาก audit trail) — ใช้เป็น fallback ของจุดจบงานบนแผนที่
   *  ถ้า completionLocation (job_tracking) ไม่ถูกบันทึกไว้ */
  auditEndGps?: string | null;
  auditEndTime?: string | null;
  completionLocation?: CompletionLocation | null;
  /** Real, live GPS ping written continuously by the driver's own device (see useLiveLocationBroadcast). */
  driverLocation?: { lat: number; lng: number; updatedAt: string; accuracy?: number } | null;
}

/** UI state exposed to the consumer component for overlay rendering. */
export type TrackingUIState =
  | { mode: 'idle' }
  | { mode: 'no-location' }            // in_progress but no GPS yet
  | { mode: 'live'; updatedAt: string; accuracy?: number } // in_progress with live position
  | { mode: 'completed'; location: CompletionLocation }
  | { mode: 'no-completion-data' }     // completed but location unknown
  | { mode: 'pending' };               // job not started

export interface UseDriverTrackingResult {
  /** Attach this ref to the <div> that Leaflet should render into. */
  mapRef: RefObject<HTMLDivElement>;
  /** Current UI state — drives overlay badges / labels outside the map canvas. */
  trackingState: TrackingUIState;
}

// ─── Constants ────────────────────────────────────────────────────────────────
// โหลด Leaflet จาก cdnjs (Cloudflare) — เสถียร/เร็วกว่า unpkg.com ที่บางครั้ง
// โหลดไม่ติด ทำให้ window.L เป็น undefined แล้วแผนที่ขึ้นขาว (ไม่มีปุ่มซูม)
const LEAFLET_CSS_URL = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
const LEAFLET_JS_URL  = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';

// ── Tile provider (ตั้งค่าได้ผ่าน env ตอน build) ──────────────────────────────
// ค่าเริ่มต้น = OpenStreetMap (ฟรี แต่ห้ามใช้เชิงพาณิชย์หนัก ๆ) — production
// บริษัทควรตั้ง VITE_TILE_URL เป็นผู้ให้บริการที่มี API key (เช่น Mapbox) ใน .env
//   VITE_TILE_URL="https://api.mapbox.com/styles/v1/.../{z}/{x}/{y}?access_token=XXX"
//   VITE_TILE_ATTRIBUTION="© Mapbox © OpenStreetMap"
const TILE_URL =
  (import.meta.env.VITE_TILE_URL as string | undefined) ||
  'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIBUTION =
  (import.meta.env.VITE_TILE_ATTRIBUTION as string | undefined) ||
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
const LOCAL_POLL_INTERVAL = 3_000; // ms — how often we re-read the driver's real GPS from storage
const DEFAULT_LAT     = 13.7279; // Bangkok (Siam area)
const DEFAULT_LNG     = 100.5354;

// How long after mounting the map we keep nudging Leaflet to recompute its
// size. Needs to comfortably outlast the Job Detail Modal's open transition
// (backdrop/scale-in), otherwise the map can be initialized against a
// container that's still 0×0 or mid-resize and render blank.
const INVALIDATE_SIZE_RETRY_DELAYS_MS = [50, 150, 300, 600, 1000];

// ─── Module-level Leaflet loader (singleton across all hook instances) ────────
let _leafletLoaderPromise: Promise<void> | null = null;

function loadLeaflet(): Promise<void> {
  if (window.L) return Promise.resolve();
  if (_leafletLoaderPromise) return _leafletLoaderPromise;

  _leafletLoaderPromise = new Promise<void>((resolve, reject) => {
    // CSS — inject once
    if (!document.getElementById('leaflet-css')) {
      const link  = document.createElement('link');
      link.id     = 'leaflet-css';
      link.rel    = 'stylesheet';
      link.href   = LEAFLET_CSS_URL;
      document.head.appendChild(link);
    }
    // JS
    const s   = document.createElement('script');
    s.src     = LEAFLET_JS_URL;
    s.onload  = () => resolve();
    s.onerror = () => {
      _leafletLoaderPromise = null; // allow retry
      reject(new Error('[useDriverTracking] Failed to load Leaflet.js'));
    };
    document.head.appendChild(s);
  });

  return _leafletLoaderPromise;
}

// ─── SVG icon factories (exact SVG from original tracking.js) ────────────────
function driverIconSvg(): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="50" viewBox="0 0 40 50">
    <circle cx="20" cy="20" r="18" fill="#7B2FBE" fill-opacity="0.18"/>
    <circle cx="20" cy="20" r="12" fill="#7B2FBE"/>
    <circle cx="20" cy="20" r="5.5" fill="#C9A84C"/>
    <polygon points="20,48 13,31 27,31" fill="#7B2FBE"/>
  </svg>`;
}

function completedIconSvg(): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="46" viewBox="0 0 36 46">
    <circle cx="18" cy="18" r="16" fill="#065F46" fill-opacity="0.15"/>
    <circle cx="18" cy="18" r="11" fill="#065F46"/>
    <path d="M12 18l4 4 8-8" stroke="#6EE7B7" stroke-width="2.5"
          stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    <polygon points="18,44 12,29 24,29" fill="#065F46"/>
  </svg>`;
}

// ─── API helpers (แทนที่ localStorage เดิม — ข้อมูล tracking มาจาก
// api/tracking.php ซึ่งอ่าน/เขียนตาราง jobs ในฐานข้อมูลจริง) ────────────────
async function fetchTrackingSnapshot(jobId: string): Promise<{
  status: TrackingJob['status'];
  driverLocation: TrackingJob['driverLocation'];
  completionLocation: CompletionLocation | null;
} | null> {
  try {
    const snap = await trackingApi.get(jobId);
    return {
      status: snap.status,
      driverLocation: snap.driverLocation ?? null,
      completionLocation: snap.completionLocation ?? null,
    };
  } catch {
    return null;
  }
}

async function persistCompletionLocation(jobId: string, loc: CompletionLocation): Promise<void> {
  try {
    await trackingApi.saveCompletion(jobId, loc);
  } catch {
    // ไม่ critical — แผนที่ยังแสดงจุดจบงานได้จากค่าที่คำนวณในเครื่องอยู่แล้ว
  }
}

// ─── GPS string parser ("lat, lng" → { lat, lng }) ───────────────────────────
function parseGpsString(gps: string): { lat: number; lng: number } | null {
  const parts = gps.split(',');
  if (parts.length !== 2) return null;
  const lat = parseFloat(parts[0].trim());
  const lng = parseFloat(parts[1].trim());
  if (isNaN(lat) || isNaN(lng)) return null;
  return { lat, lng };
}

// ─── Timestamp formatter (identical to original fmtTs) ───────────────────────
export function fmtTs(v: unknown): string {
  if (!v) return '—';
  try {
    const d =
      v != null && typeof (v as { toDate?: unknown }).toDate === 'function'
        ? (v as { toDate(): Date }).toDate()
        : new Date(v as string);
    return d.toLocaleString('th-TH', {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return String(v);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
//  The hook
// ─────────────────────────────────────────────────────────────────────────────
export function useDriverTracking(job: TrackingJob | null): UseDriverTrackingResult {
  const mapRef   = useRef<HTMLDivElement>(null);
  const [trackingState, setTrackingState] = useState<TrackingUIState>({ mode: 'idle' });

  // Internal refs — never trigger re-renders
  const leafletMapRef    = useRef<LeafletMap | null>(null);
  const leafletMarkerRef = useRef<LeafletMarker | null>(null);
  const localPollTimerRef = useRef<number | null>(null);
  const unsubscribeRef   = useRef<(() => void) | null>(null);
  const destroyedRef     = useRef(false); // guard against setState after unmount
  // Pending invalidateSize() retry timers for the map currently mounted —
  // cleared whenever we tear down/replace the map so stale timers can't
  // fire invalidateSize() on a map instance that no longer exists.
  const invalidateTimersRef = useRef<number[]>([]);
  // ResizeObserver ของกล่องแผนที่ปัจจุบัน — เรียก invalidateSize() ทุกครั้งที่
  // กล่องได้ขนาด/เปลี่ยนขนาด (แก้กรณี modal เปิดด้วย animation หรือรูปด้านบน
  // โหลดช้า ทำให้ container ได้ขนาดจริงหลัง retry ที่ตั้งเวลาไว้ → แผนที่ขาว)
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const clearInvalidateTimers = useCallback(() => {
    invalidateTimersRef.current.forEach((id) => window.clearTimeout(id));
    invalidateTimersRef.current = [];
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
  }, []);

  // ── Helpers that update marker position (live mode) ──────────────────────
  const updateMarker = useCallback((lat: number, lng: number, updatedAt: string, accuracy?: number) => {
    if (leafletMarkerRef.current && leafletMapRef.current) {
      leafletMarkerRef.current.setLatLng([lat, lng]);
      leafletMapRef.current.panTo([lat, lng]);
    }
    if (!destroyedRef.current) {
      setTrackingState({ mode: 'live', updatedAt, accuracy });
    }
  }, []);

  // ── Leaflet map initializer (called after Leaflet is loaded) ─────────────
  const initMap = useCallback(
    async (
      mapType: 'live' | 'completed',
      lat: number,
      lng: number,
    ) => {
      const el = mapRef.current;
      if (!el || destroyedRef.current) return;

      await loadLeaflet();
      if (destroyedRef.current || !window.L) return;
      const L = window.L;

      // Destroy any previous map instance attached to this div, and cancel
      // any invalidateSize() retries that were scheduled for it.
      clearInvalidateTimers();
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current  = null;
        leafletMarkerRef.current = null;
      }

      const map = L.map(el, {
        center:           [lat, lng],
        zoom:             15,
        zoomControl:      true,
        attributionControl: true,
      });

      L.tileLayer(TILE_URL, {
        attribution: TILE_ATTRIBUTION,
        maxZoom: 19,
      }).addTo(map);

      const icon = L.divIcon({
        html:       mapType === 'live' ? driverIconSvg() : completedIconSvg(),
        className:  '',
        iconSize:   mapType === 'live' ? [40, 50] : [36, 46],
        iconAnchor: mapType === 'live' ? [20, 48] : [18, 44],
      });

      const marker = L.marker([lat, lng], { icon }).addTo(map);

      leafletMapRef.current   = map;
      leafletMarkerRef.current = marker;

      // ResizeObserver: ให้แผนที่โหลด tile เต็มกล่องเสมอเมื่อ container ได้ขนาด
      // จริง (ครอบคลุมกรณีที่ modal เปิดช้า/รูปด้านบนดันเลย์เอาต์ ทำให้ retry
      // ตามเวลาไม่ทัน) — นี่คือวิธีมาตรฐานแก้ Leaflet ขึ้นขาวใน modal
      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(() => {
          if (!destroyedRef.current && leafletMapRef.current === map) map.invalidateSize();
        });
        ro.observe(el);
        resizeObserverRef.current = ro;
      }

      // FIX: The Job Detail Modal opens with a CSS transition, so the map's
      // container can still be 0×0 (or mid-resize) on the very first paint
      // after mount. A single requestAnimationFrame + invalidateSize() was
      // racing that transition and losing, leaving Leaflet permanently
      // convinced the container was zero-sized (blank white map). Instead,
      // re-run invalidateSize() + re-center on a short schedule that spans
      // the modal's open animation, so whichever attempt lands after layout
      // has settled fixes the map for good.
      requestAnimationFrame(() => {
        if (destroyedRef.current || leafletMapRef.current !== map) return;
        map.invalidateSize();
        map.panTo([lat, lng]);
      });
      INVALIDATE_SIZE_RETRY_DELAYS_MS.forEach((delay) => {
        const timerId = window.setTimeout(() => {
          // Guard against firing after unmount or after a newer map replaced this one.
          if (destroyedRef.current || leafletMapRef.current !== map) return;
          map.invalidateSize();
          map.panTo([lat, lng]);
        }, delay);
        invalidateTimersRef.current.push(timerId);
      });
    },
    [mapRef, clearInvalidateTimers],
  );

  // ── Firestore live mode ───────────────────────────────────────────────────
  const startFirestore = useCallback(
    (jobId: string) => {
      if (!window.db) {
        void startLocalTrackingMode(jobId);
        return;
      }
      const ref = window.db.collection('jobs').doc(jobId);

      unsubscribeRef.current = ref.onSnapshot(
        async (snap) => {
          if (!snap.exists || destroyedRef.current) return;
          const d      = snap.data() as Record<string, unknown>;
          const status = d.status as string;
          const loc    = d.driverLocation as (LeafletLatLng & { updatedAt?: string; accuracy?: number }) | undefined;

          if (status === 'in_progress') {
            if (loc && typeof loc.lat === 'number') {
              if (!leafletMapRef.current) {
                await initMap('live', loc.lat, loc.lng);
              } else {
                updateMarker(loc.lat, loc.lng, loc.updatedAt ?? new Date().toISOString(), loc.accuracy);
              }
            } else {
              if (!destroyedRef.current) setTrackingState({ mode: 'no-location' });
            }
            return;
          }

          // Job transitioned out of in_progress — stop Firestore listener
          stopFirestoreListener();

          if (status === 'completed') {
            const existing = d.completionLocation as CompletionLocation | undefined;
            if (!existing && loc && typeof loc.lat === 'number') {
              const syntheticLoc: CompletionLocation = {
                lat: loc.lat,
                lng: loc.lng,
                savedAt: (loc.updatedAt as string | undefined) ?? new Date().toISOString(),
                accuracy: (loc as { accuracy?: number }).accuracy,
              };
              try {
                await ref.update({ completionLocation: syntheticLoc });
                const fresh = await ref.get();
                const freshLoc = (fresh.data() as Record<string, unknown>).completionLocation as CompletionLocation;
                if (!destroyedRef.current) {
                  setTrackingState({ mode: 'completed', location: freshLoc });
                  await initMap('completed', freshLoc.lat, freshLoc.lng);
                }
              } catch {
                if (!destroyedRef.current) {
                  setTrackingState({ mode: 'completed', location: syntheticLoc });
                  await initMap('completed', syntheticLoc.lat, syntheticLoc.lng);
                }
              }
            } else if (existing) {
              if (!destroyedRef.current) {
                setTrackingState({ mode: 'completed', location: existing });
                await initMap('completed', existing.lat, existing.lng);
              }
            } else {
              if (!destroyedRef.current) setTrackingState({ mode: 'no-completion-data' });
            }
          }
        },
        (err) => {
          console.warn('[useDriverTracking] Firestore error:', err);
        },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initMap, updateMarker],
  );

  // ── API-polling live mode (no Firestore backend configured) ──────────────
  // อ่านตำแหน่ง GPS จริงของคนขับ (เขียนต่อเนื่องผ่าน useLiveLocationBroadcast
  // ที่ฝั่งหน้าใบงานคนขับ -> api/job_public.php -> ตาราง jobs ในฐานข้อมูล)
  // โดย poll จาก api/tracking.php ทุก LOCAL_POLL_INTERVAL แทนการอ่าน
  // localStorage แบบเดิม (สถาปัตยกรรม/พฤติกรรมเดิมทุกประการ แค่เปลี่ยน
  // แหล่งข้อมูลจากเบราว์เซอร์เป็นฐานข้อมูลจริงที่ทุกอุปกรณ์มองเห็นร่วมกัน)
  const startLocalTrackingMode = useCallback(
    async (jobId: string) => {
      const snap = await fetchTrackingSnapshot(jobId);
      if (destroyedRef.current) return;
      if (!snap) {
        setTrackingState({ mode: 'no-location' });
        return;
      }

      let lat = DEFAULT_LAT;
      let lng = DEFAULT_LNG;

      if (snap.driverLocation && typeof snap.driverLocation.lat === 'number') {
        lat = snap.driverLocation.lat;
        lng = snap.driverLocation.lng;
      } else if (snap.status === 'in_progress') {
        // เผื่อไว้: ยังไม่มี live ping เข้ามาเลย ใช้จุดเริ่มงานเป็นจุดตั้งต้นแทน
        // (auditStartGps อ่านจาก job prop ที่ส่งเข้ามาโดยตรง ไม่ต้องพึ่ง snapshot)
      }

      if (snap.driverLocation) {
        setTrackingState({ mode: 'live', updatedAt: snap.driverLocation.updatedAt, accuracy: snap.driverLocation.accuracy });
        initMap('live', lat, lng);
      } else {
        setTrackingState({ mode: 'no-location' });
      }

      localPollTimerRef.current = window.setInterval(async () => {
        const fresh = await fetchTrackingSnapshot(jobId);
        if (!fresh || destroyedRef.current) return;

        if (fresh.status === 'in_progress' && fresh.driverLocation) {
          lat = fresh.driverLocation.lat;
          lng = fresh.driverLocation.lng;
          if (!leafletMapRef.current) {
            initMap('live', lat, lng);
            setTrackingState({ mode: 'live', updatedAt: fresh.driverLocation.updatedAt, accuracy: fresh.driverLocation.accuracy });
          } else {
            updateMarker(lat, lng, fresh.driverLocation.updatedAt, fresh.driverLocation.accuracy);
          }
        }

        // ตรวจสอบว่างานถูกกด "จบงาน" โดยคนขับหรือยัง
        if (fresh.status === 'completed') {
          stopLocalPollTimer();

          const completionLoc: CompletionLocation =
            fresh.completionLocation ??
            { lat, lng, savedAt: new Date().toISOString(), accuracy: fresh.driverLocation?.accuracy };

          if (!fresh.completionLocation) {
            void persistCompletionLocation(jobId, completionLoc);
          }

          if (!destroyedRef.current) {
            // แผนที่จบงานถูกสร้างใน useEffect (หลัง <div> mount)
            setTrackingState({ mode: 'completed', location: completionLoc });
          }
        }
      }, LOCAL_POLL_INTERVAL);
    },
    [initMap, updateMarker],
  );

  // ── Cleanup helpers ───────────────────────────────────────────────────────
  const stopLocalPollTimer = useCallback(() => {
    if (localPollTimerRef.current !== null) {
      clearInterval(localPollTimerRef.current);
      localPollTimerRef.current = null;
    }
  }, []);

  const stopFirestoreListener = useCallback(() => {
    if (typeof unsubscribeRef.current === 'function') {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
  }, []);

  const destroyMap = useCallback(() => {
    clearInvalidateTimers();
    if (leafletMapRef.current) {
      leafletMapRef.current.remove(); // Leaflet cleanup: removes DOM events + layers
      leafletMapRef.current   = null;
      leafletMarkerRef.current = null;
    }
  }, [clearInvalidateTimers]);

  const fullCleanup = useCallback(() => {
    stopFirestoreListener();
    stopLocalPollTimer();
    destroyMap();
  }, [stopFirestoreListener, stopLocalPollTimer, destroyMap]);

  // ── Main effect — triggers whenever job identity or status changes ─────────
  useEffect(() => {
    destroyedRef.current = false;

    // Always clean up before setting up a new session (mirrors original stop())
    fullCleanup();

    if (!job) {
      setTrackingState({ mode: 'idle' });
      return () => { destroyedRef.current = true; fullCleanup(); };
    }

    const { id, status, completionLocation } = job;

    if (status === 'completed') {
      // ── Static map: completed job ─────────────────────────────────────────
      // fallback GPS จุดจบงานจาก audit trail (audit_end_gps) — ใช้เมื่อ
      // job_tracking.completion ไม่ถูกบันทึก (เช่น แอดมินไม่ได้เปิดดู live
      // ตอนคนขับจบงานพอดี) แต่คนขับเปิด GPS และมีพิกัดใน audit อยู่แล้ว
      const auditEnd = job.auditEndGps ? parseGpsString(job.auditEndGps) : null;

      if (completionLocation && typeof completionLocation.lat === 'number') {
        // แค่ตั้ง state — การสร้างแผนที่ทำใน useEffect ด้านล่าง (หลัง <div> mount)
        setTrackingState({ mode: 'completed', location: completionLocation });
      } else if (auditEnd) {
        setTrackingState({
          mode: 'completed',
          location: {
            lat: auditEnd.lat,
            lng: auditEnd.lng,
            savedAt: job.auditEndTime ?? new Date().toISOString(),
          },
        });
      } else {
        // เผื่อกรณี prop ที่ส่งเข้ามายังไม่ทันอัปเดต (poll รอบถัดไปของหน้า
        // แอดมินยังมาไม่ถึง) — ลองถามเซิร์ฟเวอร์ตรง ๆ อีกครั้งหนึ่ง
        setTrackingState({ mode: 'no-completion-data' });
        void fetchTrackingSnapshot(id).then((snap) => {
          if (destroyedRef.current || !snap?.completionLocation) return;
          setTrackingState({ mode: 'completed', location: snap.completionLocation });
        });
      }

      return () => { destroyedRef.current = true; fullCleanup(); };
    }

    if (status === 'in_progress') {
      // ── Live tracking: prefer Firestore, fall back to API-polling mode ────
      setTrackingState({ mode: 'no-location' }); // initial "awaiting GPS" shell
      startFirestore(id);

      return () => { destroyedRef.current = true; fullCleanup(); };
    }

    // pending / other statuses — no map
    setTrackingState({ mode: 'pending' });
    return () => { destroyedRef.current = true; fullCleanup(); };

  // Re-run when job ID or status changes; not when callbacks change (they're stable refs)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, job?.status]);

  // สร้างแผนที่ "จบงาน" หลัง React commit กล่องแผนที่แล้วเท่านั้น
  // แก้อาการแผนที่จบงานขึ้นขาว: เดิม initMap ถูกเรียกทันทีหลัง setState ทำให้
  // กล่องแผนที่ยังไม่ mount (mapRef.current เป็น null) Leaflet เลยไม่ init
  // useEffect นี้รันหลัง DOM commit จึงมั่นใจว่า container พร้อมใช้งานแล้ว
  // (dep เป็นพิกัด จะ init ใหม่ต่อเมื่อเปลี่ยนใบงาน/ตำแหน่งจริงเท่านั้น)
  const completedLat = trackingState.mode === 'completed' ? trackingState.location.lat : null;
  const completedLng = trackingState.mode === 'completed' ? trackingState.location.lng : null;
  useEffect(() => {
    if (completedLat == null || completedLng == null || destroyedRef.current) return;
    initMap('completed', completedLat, completedLng);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedLat, completedLng]);

  return { mapRef, trackingState };
}
